import { useAccountsInfo, type AccountType } from '@/entities/account';
import {
  useInternalTransferAssets,
  useInternalTransferValues,
  type AccountOption,
  type AssetOption,
  type InternalAccountSectionCardType,
  type InternalTransferAccountsMap,
  type InternalTransferFormValues,
  type SectionType,
} from '../model';
import type { TransferInternalSettingsDictionary } from '@/shared/types';
import { type UseFormReturn } from 'react-hook-form';
import { formatAmount, useAppSettingsStore } from '@/shared/lib';
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type Dispatch,
  type SetStateAction,
} from 'react';
import { useAssetsSymbolsDictionary } from '@/shared/api';
import { cn } from '@/shared/utils';
import { useInternalTransferInfoMessage } from '../model/useInternalTransferInfoMessage';
import InternalTransferAccountCardHeader from './InternalTransferAccountCardHeader';
import { InternalTransferInfoMessage } from './InternalTransferInfoMessage';
import InternalTransferAccountCardSelect from './InternalTransferAccountCardSelect';
import { NUMERIC_CONFIG } from '@/shared/config';

interface InternalTransferAccountCardProps {
  account: InternalAccountSectionCardType;
  assetsIconsData?: Record<string, string>;
  selectedAccountTypeFrom?: AccountType | null;
  selectedAccountTypeTo?: AccountType | null;
  selectedAssetFrom?: AssetOption | undefined;
  sectionType: SectionType;
  transferInternalSettings?: TransferInternalSettingsDictionary;
  form: UseFormReturn<InternalTransferFormValues>;
  setSelectedAssetFrom?: Dispatch<SetStateAction<AssetOption | undefined>>;
  setSelectedAssetTo?: Dispatch<SetStateAction<AssetOption | undefined>>;
  setSelectedAccountFrom?: Dispatch<SetStateAction<AccountOption | undefined>>;
  setSelectedAccountTo?: Dispatch<SetStateAction<AccountOption | undefined>>;
  accountOptions?: AccountOption[];
}

function InternalTransferAccountCard({
  account,
  assetsIconsData,
  selectedAccountTypeFrom,
  selectedAccountTypeTo,
  selectedAssetFrom,
  sectionType,
  transferInternalSettings,
  form,
  setSelectedAssetFrom,
  setSelectedAssetTo,
  setSelectedAccountFrom,
  setSelectedAccountTo,
  accountOptions,
}: InternalTransferAccountCardProps) {
  const { baseAsset } = useAppSettingsStore();
  const { accountsInfo } = useAccountsInfo();
  const { assetsSymbolsData } = useAssetsSymbolsDictionary();
  const language = useAppSettingsStore.getState().language || 'ru';

  const [selectedValue, setSelectedValue] = useState<string>('');

  const {
    placeholder,
    watchedAccNumFrom,
    watchedAccNumTo,
    watchedAssetFromCode,
    watchedAssetToCode,
    watchedAccNum,
    watchedAssetCode,
    accNumFieldName,
    assetFieldName,
    errors,
  } = useInternalTransferValues({ form, sectionType });

  const isInternalUpdateRef = useRef(false);
  const prevValuesRef = useRef({
    assetCode: '',
    accNum: '',
  });
  const isAssetSelector = account.type === 'spot' || account.type === 'ewallet';

  const accountInfoMap = useMemo<InternalTransferAccountsMap>(() => {
    if (!accountsInfo?.accounts) return new Map();
    return new Map(accountsInfo.accounts.map(acc => [acc.accNum, acc]));
  }, [accountsInfo]);

  const { assetSelectOptions, accountWithAssetOptions } =
    useInternalTransferAssets({
      account,
      sectionType,
      isAssetSelector,
      baseAsset,
      assetsIconsData,
      assetsSymbolsData,
      transferInternalSettings,
      selectedAccountTypeFrom,
      selectedAccountTypeTo,
      watchedAssetFromCode,
      watchedAssetToCode,
      watchedAccNumFrom,
      watchedAccNumTo,
      accountInfoMap,
      selectedAssetFrom,
    });

  const { infoMessageConfig } = useInternalTransferInfoMessage({
    transferInternalSettings,
    accountType: account?.type,
    accountWithAssetOptions,
    assetSelectOptions,
    selectedAccountTypeFrom,
    selectedAccountTypeTo,
    watchedAssetFromCode,
    isAssetSelector,
    sectionType,
  });

  useEffect(() => {
    if (isInternalUpdateRef.current) {
      return;
    }

    const assetCodeChanged =
      prevValuesRef.current.assetCode !== watchedAssetCode;
    const accNumChanged = prevValuesRef.current.accNum !== watchedAccNum;

    if (!assetCodeChanged && !accNumChanged) {
      return;
    }

    const newValue = isAssetSelector
      ? watchedAssetCode || ''
      : watchedAccNum && watchedAssetCode
        ? `${watchedAccNum}_${watchedAssetCode}`
        : '';

    form.setValue(
      sectionType === 'from' ? 'assetFrom' : 'assetTo',
      isAssetSelector ? newValue : newValue.split('_')[1],
      {
        shouldValidate: false,
        shouldDirty: false,
      }
    );

    setSelectedValue(prev => {
      return prev !== newValue ? newValue : prev;
    });

    prevValuesRef.current = {
      assetCode: watchedAssetCode || '',
      accNum: watchedAccNum || '',
    };
  }, [watchedAssetCode, watchedAccNum, isAssetSelector]);

  const handleValueChange = useCallback(
    (value: string) => {
      isInternalUpdateRef.current = true;
      setSelectedValue(value);

      const finishInternalUpdate = () => {
        requestAnimationFrame(() => {
          isInternalUpdateRef.current = false;
        });
      };

      const setSelectedAsset =
        sectionType === 'from' ? setSelectedAssetFrom : setSelectedAssetTo;

      const setSelectedAccount =
        sectionType === 'from' ? setSelectedAccountFrom : setSelectedAccountTo;

      const resetAmount = () => {
        form.setValue('amount', '', {
          shouldValidate: false,
          shouldDirty: true,
        });
      };

      let selectedOption: AssetOption | undefined;
      let asset: string = '';

      if (isAssetSelector) {
        if (!value) {
          finishInternalUpdate();
          return;
        }

        selectedOption = account.data?.accounts[0].assets.find(
          opt => opt.code === value
        ) as AssetOption;

        asset = value;
        form.setValue(assetFieldName, value, {
          shouldValidate: true,
          shouldDirty: true,
        });

        if (
          watchedAccNum === '' &&
          account.data?.accounts[0].accNum &&
          setSelectedAccount
        ) {
          form.setValue(accNumFieldName, account.data?.accounts[0].accNum, {
            shouldValidate: false,
            shouldDirty: true,
          });
          setSelectedAccount(
            accountOptions?.find(
              acc => acc.accNum === account.data?.accounts[0].accNum
            )
          );
        }
      } else {
        const [accNum, assetCode] = value.split('_');

        if (!accNum || !assetCode) {
          finishInternalUpdate();
          return;
        }

        selectedOption = accountWithAssetOptions.find(
          acc => acc?.accNum === accNum && acc.code === assetCode
        ) as AssetOption;

        if (selectedOption && setSelectedAccount) {
          setSelectedAccount(
            accountOptions?.find(acc => acc.accNum === accNum)
          );
        }

        form.setValue(accNumFieldName, accNum, {
          shouldValidate: false,
          shouldDirty: true,
        });

        asset = assetCode;
        form.setValue(assetFieldName, assetCode, {
          shouldValidate: true,
          shouldDirty: true,
        });

        form.trigger(assetFieldName);
      }

      if (setSelectedAsset) {
        setSelectedAsset(
          selectedOption
            ? selectedOption
            : {
                code: asset,
                name: asset,
                precision: 0,
                available: 0,
              }
        );

        if (sectionType === 'from') {
          resetAmount();
        }
      }

      finishInternalUpdate();
    },
    [
      isAssetSelector,
      sectionType,
      assetFieldName,
      accNumFieldName,
      account,
      accountWithAssetOptions,
      form,
      setSelectedAssetFrom,
      setSelectedAssetTo,
      setSelectedAccountFrom,
      setSelectedAccountTo,
      accountOptions,
    ]
  );

  const formattedBalance = `${formatAmount(account.data?.balance ?? 0, NUMERIC_CONFIG.precision.default)} ${baseAsset}`;

  return (
    <div
      className={cn(
        `flex flex-col items-start p-[1.6rem] gap-[1.6rem] w-full h-[15.6rem] bg-background rounded-[2.4rem] shadow-smooth flex-none flex-grow-0`,
        infoMessageConfig.show ? ' pointer-events-none opacity-60' : ''
      )}>
      <InternalTransferAccountCardHeader
        account={account}
        formattedBalance={formattedBalance}
      />

      {infoMessageConfig.show ? (
        <InternalTransferInfoMessage
          translationKey={infoMessageConfig.translationKey}
          linkAction={infoMessageConfig.linkAction}
          linkTarget={
            typeof infoMessageConfig.linkTarget === 'function'
              ? infoMessageConfig.linkTarget(language)
              : infoMessageConfig.linkTarget
          }
        />
      ) : (
        <>
          {(isAssetSelector
            ? assetSelectOptions.length > 0
            : accountWithAssetOptions.length > 0) && (
            <div className="w-full">
              <InternalTransferAccountCardSelect
                accountType={account?.type}
                isAssetSelector={isAssetSelector}
                assetSelectOptions={assetSelectOptions}
                accountWithAssetOptions={accountWithAssetOptions.map(opt => ({
                  ...opt,
                  icon: assetsIconsData?.[opt.code as string] || '',
                }))}
                placeholder={placeholder}
                selectedValue={selectedValue}
                handleValueChange={handleValueChange}
                baseAsset={baseAsset}
                sectionType={sectionType}
                assetFromCode={watchedAssetFromCode}
                isInvalid={Boolean(
                  errors?.[sectionType === 'from' ? 'assetFrom' : 'assetTo']
                    ?.type
                )}
              />
            </div>
          )}
        </>
      )}
    </div>
  );
}

export default InternalTransferAccountCard;
